import { Box, Text, Group, Button, TextInput, PasswordInput, Select, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';

import { useGetWithdrawalMethods } from '@/modules/withdrawal-methods';
import { useCreatePaymentSystems } from '../hooks/use-create-payment-systems';
import { useUpdatePaymentSystem } from '../hooks/use-update-payment-systems';
import { useDeletePaymentSystem } from '../hooks/use-delete-payment-systems';

import type { TPaymentSystem } from '../store/types';

type MexcSettings = {
  api_url?: string;
  api_key?: string;
  secret_key?: string;
  payout_methods?: Record<string, string>;
};

type FormData = {
  apiUrl: string;
  apiKey: string;
  secretKey: string;
  payoutMethods: { methodId: string; network: string }[];
};

type Props = {
  item?: TPaymentSystem;
  onClose?: () => void;
};

export const MexcPaymentSystem = (props: Props) => {
  const {
    item,
    onClose,
  } = props;

  const {
    data: methods,
    isLoading: isMethodsLoading,
  } = useGetWithdrawalMethods();

  const {
    createPaymentSystem,
    isLoading: isCreateLoading,
  } = useCreatePaymentSystems();

  const {
    updatePaymentSystem,
    isLoading: isUpdateLoading,
  } = useUpdatePaymentSystem();

  const {
    deletePaymentSystem,
    isLoading: isDeleteLoading,
  } = useDeletePaymentSystem();

  const settings: MexcSettings = JSON.parse(item?.data || '{}');

  const form = useForm<FormData>({
    mode: 'uncontrolled',
    initialValues: {
      apiUrl: settings.api_url || 'https://api.mexc.com',
      apiKey: settings.api_key || '',
      secretKey: settings.secret_key || '',
      payoutMethods: Object.entries(settings.payout_methods || {}).map(([methodId, network]) => ({ methodId, network })),
    },
    validate: {
      apiUrl: (value) => /^https:\/\/[^\s]+$/.test(value) ? null : 'Введите HTTPS адрес API',
      apiKey: (value) => value.trim() ? null : 'Введите API key',
      secretKey: (value) => value.trim() ? null : 'Введите Secret key',
      payoutMethods: {
        methodId: (value, values) => {
          if (!value) {
            return 'Выберите метод вывода';
          }

          return values.payoutMethods.filter((method) => method.methodId === value).length > 1
            ? 'Метод уже добавлен'
            : null;
        },
        network: (value) => value.trim() ? null : 'Введите код сети MEXC',
      },
    },
  });

  const saveSettings = async (values: FormData) => {
    const params = {
      name: 'mexc',
      data: {
        api_url: values.apiUrl,
        api_key: values.apiKey.trim(),
        secret_key: values.secretKey.trim(),
        payout_methods: Object.fromEntries(values.payoutMethods.map(({ methodId, network }) => [methodId, network.trim()])),
      },
    };

    try {
      if (item) {
        await updatePaymentSystem({ ...params, systemId: item.id }).unwrap();
      } else {
        await createPaymentSystem(params).unwrap();
      }

      onClose?.();
    } catch {
      return;
    }
  };

  const handleSubmit = (values: FormData) => {
    modals.openConfirmModal({
      title: 'Сохранение настроек MEXC',
      centered: true,
      children: 'После сохранения подтверждение вывода по выбранным методам отправит выплату с аккаунта MEXC.',
      labels: { confirm: 'Сохранить', cancel: 'Отмена' },
      onConfirm: () => saveSettings(values),
    });
  };

  const openDeleteModal = () => modals.openConfirmModal({
    title: 'Удаление платёжной системы',
    centered: true,
    children: 'Вы точно хотите удалить платёжную систему MEXC?',
    labels: { confirm: 'Удалить', cancel: 'Отмена' },
    confirmProps: { color: 'red' },
    onConfirm: async () => item && await deletePaymentSystem({ systemId: item.id }),
  });

  const availableMethods = (methods || []).map((method) => ({
    value: String(method.id),
    label: `${method.name} (${method.currency})`,
  }));

  return (
    <Box>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <Text size="sm">
            Ключу MEXC нужны разрешения на вывод средств и чтение истории выплат.
          </Text>
          <TextInput
            label="Адрес API"
            withAsterisk
            key={form.key('apiUrl')}
            {...form.getInputProps('apiUrl')}
          />
          <PasswordInput
            label="API key"
            withAsterisk
            key={form.key('apiKey')}
            {...form.getInputProps('apiKey')}
          />
          <PasswordInput
            label="Secret key"
            withAsterisk
            key={form.key('secretKey')}
            {...form.getInputProps('secretKey')}
          />

          <Text size="sm">
            Подтверждение вывода по выбранным методам запускает выплату MEXC. Остальные методы обрабатываются вручную.
          </Text>
          {form.getValues().payoutMethods.map((_, index) => (
            <Group key={form.key(`payoutMethods.${index}`)} align="flex-start">
              <Select
                label="Метод вывода"
                data={availableMethods}
                disabled={isMethodsLoading}
                searchable
                withAsterisk
                flex={1}
                key={form.key(`payoutMethods.${index}.methodId`)}
                {...form.getInputProps(`payoutMethods.${index}.methodId`)}
              />
              <TextInput
                label="Код сети MEXC"
                placeholder="TRX"
                withAsterisk
                flex={1}
                key={form.key(`payoutMethods.${index}.network`)}
                {...form.getInputProps(`payoutMethods.${index}.network`)}
              />
              <Button
                mt={25}
                color="red"
                variant="outline"
                onClick={() => form.removeListItem('payoutMethods', index)}
              >
                Удалить
              </Button>
            </Group>
          ))}
          <Button
            variant="outline"
            onClick={() => form.insertListItem('payoutMethods', { methodId: '', network: 'TRX' })}
          >
            Добавить метод
          </Button>
        </Stack>

        <Group justify="flex-start" mt="30">
          {item ? (
            <Button color="red" loading={isDeleteLoading} onClick={openDeleteModal}>
              Удалить
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose}>
              Отмена
            </Button>
          )}
          <Button type="submit" loading={isCreateLoading || isUpdateLoading}>
            Сохранить
          </Button>
        </Group>
      </form>
    </Box>
  );
};
